/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @nx/enforce-module-boundaries */
'use client';

import { Icons, Stack } from '@castlery/fortress';
import { useTrackingTags } from '@castlery/modules-tracking-components';
import { generateVideoThumbnail, generateVideoUrl, type ThumbnailOptions, type VideoOptions } from '@castlery/utils';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { default as ReactPlayerType } from 'react-player';
import { FortressImage } from '../fortress-image/fortress-image';
import { logger } from '@castlery/observability';

// 动态导入ReactPlayer
const VideoPlayer = dynamic(() => import('./components/video-player'), {
  ssr: false,
});

// 定义播放器事件类型
interface VideoPlayerEvent {
  type: string;
  target?: HTMLVideoElement;
  currentTime?: number;
  duration?: number;
  player?: any;
  [key: string]: any;
}

export interface FortressVideoProps {
  /** 视频源 URL 或 Cloudinary 公共 ID */
  src: string;
  /** 视频播放器的唯一标识符 */
  id?: string;
  /** 视频宽度 */
  width?: string | number;
  /** 视频高度 */
  height?: string | number;
  /** 视频清晰度 */
  resolution?: 480 | 720 | 1080;
  /** 是否自动播放 */
  autoPlay?: boolean;
  /** 是否显示控制器 */
  controls?: boolean;
  /** 是否静音 */
  muted?: boolean;
  /** 是否循环播放 */
  loop?: boolean;
  /** 是否在 ios 设备上内联播放 */
  playsInline?: boolean;
  /** 海报图片 - 如果不传入，将自动为 Cloudinary 视频生成缩略图 */
  poster?: string;
  /** 缩略图生成配置 */
  thumbnailConfig?: ThumbnailOptions & {
    /** 是否禁用自动缩略图生成 */
    disabled?: boolean;
  };
  /** 视频变换配置 */
  videoConfig?: VideoOptions;
  /** 视频容器样式配置 */
  containerConfig?: {
    /** 容器的宽高比 */
    aspectRatio?: number;
    /** 视频在容器中的填充方式 */
    objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
    /** 是否隐藏溢出内容 */
    overflow?: 'hidden' | 'visible' | 'auto' | 'scroll';
  };
  /** 播放器事件回调 */
  onPlay?: (event: VideoPlayerEvent) => void;
  onPause?: (event: VideoPlayerEvent) => void;
  onEnded?: (event: VideoPlayerEvent) => void;
  onError?: (event: VideoPlayerEvent) => void;
  /** 视频加载完成时的回调 */
  onReady?: (event: VideoPlayerEvent) => void;
  /** 播放进度回调 - 在 25%、50%、75%、100% 时触发 */
  onProgress?: (percentage: number, event: VideoPlayerEvent) => void;
  /** 自定义样式 */
  sx?: any;
  /** 自定义类名 */
  className?: string;
  /** 是否响应式 */
  responsive?: boolean;
  /** 追踪标签 */
  trackingTags?: Record<string, any>;
  /** 模块名称 */
  moduleName?: string;
  /** 元素名称 */
  elementName?: string;
  /** 追踪内容 */
  trackingContent?: Record<string, any>;
  /** 是否启用播放进度数据上报 */
  enableProgressTracking?: boolean;
  /** 自定义进度上报点，默认为 [25, 50, 75, 100] */
  progressTrackingPoints?: number[];
  /** 是否启用懒加载 - 当视频进入视图时才开始播放 */
  lazyLoad?: boolean;
  /** 自定义根容器，用于 IntersectionObserver 的 root 选项 */
  rootContainer?: Element | null;
  /** 自定义 IntersectionObserver 的阈值 */
  threshold?: number;
  /** 是否基于可见性自动播放 */
  autoPlayOnVisible?: boolean;
  /** 是否基于可见性自动暂停 */
  autoPauseOnVisible?: boolean;
}

const playButton = (
  <Icons.PlayWhite
    sx={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: { xs: '3.75rem', md: '7.5rem' },
      height: { xs: '3.75rem', md: '7.5rem' },
    }}
  />
);

// 合并refs的工具函数
function mergeRefs<T = any>(...refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export const FortressVideo = forwardRef<ReactPlayerType, FortressVideoProps>(
  (
    {
      src,
      id,
      width = '100%',
      height = '100%',
      resolution = 1080,
      autoPlay = false,
      controls = true,
      muted = false,
      loop = false,
      playsInline = true,
      poster,
      thumbnailConfig = {},
      videoConfig,
      containerConfig,
      onPlay,
      onPause,
      onEnded,
      onError,
      onReady,
      onProgress,
      sx,
      className,
      responsive = true,
      trackingTags,
      moduleName,
      elementName,
      trackingContent,
      enableProgressTracking = false,
      progressTrackingPoints = [25, 50, 75, 100],
      lazyLoad = false,
      rootContainer = null,
      threshold = 0.8,
      autoPlayOnVisible = false,
      autoPauseOnVisible = false,
      ...rest
    },
    ref
  ) => {
    const [watchCompleted, setWatchCompleted] = useState(false);
    const [playing, setPlaying] = useState(autoPlay);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const trackedProgressPoints = useRef<Set<number>>(new Set());
    const playerRef = useRef<ReactPlayerType & { handleClickPreview?: () => void }>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // 清理函数
    const clearProgressInterval = useCallback(() => {
      if (playerRef.current && (playerRef.current as any)._progressInterval) {
        clearInterval((playerRef.current as any)._progressInterval);
        (playerRef.current as any)._progressInterval = null;
      }
    }, []);

    // 重置状态
    useEffect(() => {
      setWatchCompleted(false);
      setPlaying(autoPlay);
      setIsVideoLoaded(false);
      trackedProgressPoints.current.clear();
      clearProgressInterval();
    }, [src, autoPlay, lazyLoad, clearProgressInterval]);

    const handleIntersect = useCallback(
      (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (lazyLoad || autoPlayOnVisible) {
              const showPreview = playerRef.current?.state?.showPreview;
              if (showPreview) {
                playerRef.current?.handleClickPreview?.();
              } else {
                setPlaying(entry.isIntersecting);
              }
              if (lazyLoad) {
                observer.unobserve(entry.target);
              }
            }
          } else {
            if (autoPauseOnVisible) {
              setPlaying(entry.isIntersecting);
            }
          }
        });
      },
      [lazyLoad, autoPlayOnVisible, autoPauseOnVisible]
    );

    useEffect(() => {
      if (lazyLoad || autoPlayOnVisible || autoPauseOnVisible) {
        if (typeof window !== 'undefined' && window.IntersectionObserver) {
          if (containerRef.current) {
            setPlaying(false);

            const observer = new IntersectionObserver(handleIntersect, {
              root: rootContainer,
              rootMargin: '0px',
              threshold,
            });

            observer.observe(containerRef.current);
            return () => observer.disconnect();
          }
        }
      }
    }, [containerRef]);

    // 视频加载完成的处理函数
    const handleReady = useCallback(
      (player: ReactPlayerType) => {
        setIsVideoLoaded(true);
        const event: VideoPlayerEvent = {
          type: 'ready',
          player,
          currentTime: player?.getCurrentTime() || 0,
          duration: player?.getDuration() || 0,
        };
        onReady?.(event);
      },
      [onReady]
    );

    // 构建视频 URL - 使用专业的 generateVideoUrl 函数
    const videoUrl = useMemo(() => {
      const finalVideoConfig = {
        width: resolution,
        quality: 'auto',
        format: 'auto',
        crop: 'fit',
        gravity: 'center',
        aspectRatio: 1,
        ...videoConfig,
      };
      return generateVideoUrl(src, finalVideoConfig);
    }, [resolution, src, videoConfig]);

    // 智能缩略图生成
    const generatedPoster = useMemo(() => {
      // 如果用户传入了 poster，优先使用
      if (poster) {
        return poster;
      }

      // 如果禁用了自动生成，返回空
      if (thumbnailConfig.disabled) {
        return undefined;
      }

      // 直接使用 generateVideoThumbnail，它已经能智能处理各种URL类型
      const thumbnailOptions: ThumbnailOptions = {
        // 直接透传所有用户配置，包含智能默认值
        thumbnailAspectRatio: '1:1',
        thumbnailStartOffset: 0,
        thumbnailCrop: 'fill',
        thumbnailGravity: 'center',
        ...thumbnailConfig,
        // 默认时间位置：0%
        ...(thumbnailConfig.thumbnailStartOffset === undefined &&
          thumbnailConfig.thumbnailPercentage === undefined && {
            thumbnailPercentage: 0,
          }),
      };

      try {
        return generateVideoThumbnail(src, thumbnailOptions);
      } catch (error) {
        console.warn('FortressVideo: 缩略图生成失败:', error);
        return undefined;
      }
    }, [src, poster, thumbnailConfig]);

    const containerOptions = useMemo(() => {
      return {
        aspectRatio: 1,
        objectFit: 'cover',
        overflow: 'hidden',
        ...containerConfig,
      };
    }, [containerConfig]);

    // 追踪标签
    const standardTrackingTags = useTrackingTags({
      moduleName: moduleName || 'fortress-video-player',
      elementName: elementName || 'video',
      content: {
        assetLink: src,
        ...(trackingContent || {}),
      },
    });

    const domTrackingAttrs = {
      ...(moduleName ? standardTrackingTags : {}),
      ...(trackingTags || {}),
    };

    // 创建标准事件对象的工具函数
    const createVideoEvent = useCallback((type: string, additionalData: any = {}): VideoPlayerEvent => {
      return {
        type,
        player: playerRef.current,
        currentTime: playerRef.current?.getCurrentTime() || 0,
        duration: playerRef.current?.getDuration() || 0,
        ...additionalData,
      };
    }, []);

    // 数据上报工具函数
    const trackVideoEvent = useCallback(
      (eventType: string, additionalData: Record<string, any> = {}) => {
        if (typeof window !== 'undefined' && window?.dataLayer) {
          window.dataLayer.push({
            event: eventType,
            video_title: src,
            ...additionalData,
          });
        }
      },
      [src]
    );

    // 播放进度跟踪函数
    const debouncedTrack = useRef(
      debounce((percentage: number, event: VideoPlayerEvent) => {
        if (watchCompleted) return;

        // 防止重复上报同一个进度点
        if (trackedProgressPoints.current.has(percentage)) return;

        // 记录已上报的进度点
        trackedProgressPoints.current.add(percentage);

        if (percentage === 100) {
          setWatchCompleted(true);
        }

        // 调用外部传入的进度回调
        onProgress?.(percentage, event);

        // 数据上报
        trackVideoEvent('trackEvent', {
          'eventDetails.category': 'Video',
          'eventDetails.action': 'video progress',
          'eventDetails.position': `${percentage}% Completion`,
          'eventDetails.label': src,
        });
      }, 1000)
    ).current;

    // 组件卸载时清理资源
    useEffect(() => {
      return () => {
        clearProgressInterval();
        // 清理 debounced 函数
        debouncedTrack.cancel?.();
      };
    }, [clearProgressInterval, debouncedTrack]);

    // 事件处理
    const handlePlay = useCallback(() => {
      setPlaying(true);
      const event = createVideoEvent('play');
      onPlay?.(event);
      // 启动播放进度监控
      if (enableProgressTracking && playerRef.current) {
        clearProgressInterval();

        const interval = setInterval(() => {
          if (playerRef.current) {
            try {
              const currentTime = playerRef.current.getCurrentTime() || 0;
              const duration = playerRef.current.getDuration() || 0;

              if (duration > 0) {
                const percentage = Math.round((currentTime / duration) * 100);
                if (progressTrackingPoints.includes(percentage)) {
                  debouncedTrack(percentage, createVideoEvent('progress', { currentTime, duration }));
                }
              }
            } catch (error) {
              console.warn('FortressVideo: Error getting player time:', error);
            }
          }
        }, 1000);

        (playerRef.current as any)._progressInterval = interval;
      }

      // 追踪播放事件
      trackVideoEvent('video_play');
    }, [
      onPlay,
      enableProgressTracking,
      progressTrackingPoints,
      debouncedTrack,
      clearProgressInterval,
      trackVideoEvent,
      createVideoEvent,
    ]);

    const handlePause = useCallback(() => {
      setPlaying(false);
      const event = createVideoEvent('pause');
      onPause?.(event);
      clearProgressInterval();

      // 追踪暂停事件
      trackVideoEvent('video_pause');
    }, [onPause, clearProgressInterval, trackVideoEvent, createVideoEvent]);

    const handleEnded = useCallback(() => {
      setPlaying(false);
      const event = createVideoEvent('ended');
      onEnded?.(event);
      clearProgressInterval();

      // 如果启用了进度追踪且包含 100%，先上报进度
      if (enableProgressTracking && progressTrackingPoints.includes(100)) {
        debouncedTrack(100, createVideoEvent('ended'));
      }

      // 总是上报完成事件
      trackVideoEvent('video_complete');
    }, [
      onEnded,
      enableProgressTracking,
      progressTrackingPoints,
      debouncedTrack,
      clearProgressInterval,
      trackVideoEvent,
      createVideoEvent,
    ]);

    const handleError = useCallback(
      (error: any) => {
        setPlaying(false);
        const event = createVideoEvent('error', { error });
        onError?.(event);
        clearProgressInterval();

        logger.error('FortressVideo playback error', {
          src,
          error,
          timestamp: new Date().toISOString(),
        });

        // 上报错误事件
        // trackVideoEvent('video_error', {
        //   error_details: error?.message || 'unknown_error',
        // });
      },
      [onError, src, clearProgressInterval, createVideoEvent]
    );

    const handleClickPreview = useCallback((args: any) => {
      setPlaying(true);
    }, []);

    return (
      <Stack
        ref={containerRef}
        className={className}
        sx={{
          width: responsive ? '100%' : width,
          height: responsive ? 0 : height || 0,
          paddingBottom: responsive ? `${(1 / containerOptions.aspectRatio) * 100}%` : 0,
          backgroundColor: 'var(--fortress-palette-brand-charcoal-800)',
          position: 'relative',
          overflow: containerOptions.overflow,
          '& .media-controls': {
            width: '100%',
            height: '100%',
          },
          '& .react-player__preview': {
            justifyContent: 'initial !important',
            alignItems: 'initial !important',
          },
          ...sx,
        }}
      >
        <VideoPlayer
          playRef={mergeRefs(playerRef, ref)}
          key={id || src}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={playing}
          controls={controls}
          muted={muted}
          loop={loop}
          playsinline={playsInline}
          style={{
            objectFit: containerOptions.objectFit as any,
            position: 'absolute',
          }}
          light={
            generatedPoster && !lazyLoad ? (
              <FortressImage
                src={generatedPoster}
                alt={'Video thumbnail'}
                // ratio={
                //   thumbnailConfig.thumbnailAspectRatio
                //     ? (parseAspectRatio(thumbnailConfig.thumbnailAspectRatio) as number) || containerOptions.aspectRatio
                //     : containerOptions.aspectRatio
                // }
                // objectFit="cover"
                sx={{
                  height: '100%',
                  '--AspectRatio-paddingBottom': 0,
                }}
              />
            ) : undefined
          }
          stopOnUnmount={true}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onError={handleError}
          onReady={handleReady}
          onClickPreview={handleClickPreview}
          playIcon={playButton}
          config={{
            file: {
              attributes: {
                controlsList: 'nodownload',
                'data-dt-id': domTrackingAttrs['data-dt-id'],
              },
            },
          }}
          {...rest}
        />
      </Stack>
    );
  }
);

export default FortressVideo;
